/*
 * Canvas Transitions Module
 *
 * This module provides transition effects used in rendering canvas frames.
 * It contains functions for various transition types:
 *   - Circle Wipe
 *   - Rainbow Wipe
 *   - Corner Wipe
 *   - Stack Transition
 *   - Color Wipe
 *
 * Each function expects a common set of parameters and a callback to render the underlying content (e.g. screenclip).
 */

import { interpolate } from "../video_effects/interpolations";

//-------------------------------
// Helper Functions
//-------------------------------

/**
 * Blends a hex color with white using the provided alpha (0 - 1).
 * @param hex - the hex color string
 * @param alpha - blending factor
 * @returns an rgba string
 */
export function blendWithWhite(hex: string, alpha: number): string {
	const hexToRgb = (_hex: string) => {
		const hex = _hex.replace("#", "");
		const bigint = Number.parseInt(hex, 16);
		return {
			r: (bigint >> 16) & 255,
			g: (bigint >> 8) & 255,
			b: bigint & 255,
		};
	};
	const { r, g, b } = hexToRgb(hex);
	const blendedR = Math.round(r * (1 - alpha) + 255 * alpha);
	const blendedG = Math.round(g * (1 - alpha) + 255 * alpha);
	const blendedB = Math.round(b * (1 - alpha) + 255 * alpha);
	return `rgba(${blendedR}, ${blendedG}, ${blendedB}, 1)`;
}

/**
 * Blends a hex color with black using the provided alpha (0 - 1).
 * @param hex - the hex color string
 * @param alpha - blending factor
 * @returns an rgba string
 */
export function blendWithBlack(hex: string, alpha: number): string {
	const hexToRgb = (_hex: string) => {
		const hex = _hex.replace("#", "");
		const bigint = Number.parseInt(hex, 16);
		return {
			r: (bigint >> 16) & 255,
			g: (bigint >> 8) & 255,
			b: bigint & 255,
		};
	};
	const { r, g, b } = hexToRgb(hex);
	const blendedR = Math.round(r * (1 - alpha));
	const blendedG = Math.round(g * (1 - alpha));
	const blendedB = Math.round(b * (1 - alpha));
	return `rgba(${blendedR}, ${blendedG}, ${blendedB}, 1)`;
}

//-------------------------------
// Common Interface
//-------------------------------

export interface TransitionParams {
	ctx: CanvasRenderingContext2D;
	time: number; // current time
	transitionDuration: number; // duration of the transition effect
	keyTime: number; // reference time (e.g., screenclip start or end time)
	canvasWidth: number;
	canvasHeight: number;
	transition: {
		type: string;
		direction?: string;
		duration: number;
		color?: string;
	};
	backgroundVisible?: boolean; // flag indicating if background edits are visible
	mode: "in" | "out";

	// Callback to draw underlying content (e.g. screenclip frame).
	// Parameters: time, opacity, scale, and optional ignoreEdits flag.
	drawUnderlying: (
		time: number,
		opacity?: number,
		ignoreEdits?: boolean,
	) => any;

	customClip?: () => void;
}

//-------------------------------
// Transition Effect Functions
//-------------------------------
export function applyCircleWipe(params: TransitionParams): any {
	const {
		ctx,
		time,
		transitionDuration,
		keyTime,
		canvasWidth,
		canvasHeight,
		mode,
		drawUnderlying,
	} = params;

	const wipeDuration = 0.6 * transitionDuration;
	const maxRadius = Math.sqrt(canvasWidth ** 2 + canvasHeight ** 2);
	const minRadius = 10;

	// Set radius interpolation based on mode
	const radius =
		mode === "in"
			? interpolate(
					{ x: keyTime - transitionDuration, y: minRadius },
					{
						x: keyTime - (transitionDuration - wipeDuration),
						y: maxRadius,
					},
					time,
					"easeInQuad",
				)
			: interpolate(
					{ x: keyTime, y: maxRadius },
					{ x: keyTime + transitionDuration, y: minRadius },
					time,
					"easeOutQuad",
				);

	// Set opacity interpolation based on mode
	const opacity =
		mode === "in"
			? interpolate(
					{ x: keyTime - transitionDuration, y: 0.8 },
					{ x: keyTime, y: 1 },
					time,
					"easeOutQuint",
				)
			: 1;

	ctx.save();
	ctx.beginPath();
	ctx.ellipse(
		canvasWidth / 2,
		canvasHeight / 2,
		radius,
		radius,
		0,
		0,
		Math.PI * 2,
		true,
	);
	ctx.closePath();
	ctx.clip();

	// Draw the underlying content *after* clipping. Opacity is applied within drawUnderlying
	drawUnderlying(time, opacity);

	ctx.restore();
}

export function applyRainbowWipe(params: TransitionParams): any {
	const {
		ctx,
		time,
		transitionDuration,
		keyTime,
		canvasWidth,
		canvasHeight,
		transition,
		backgroundVisible,
		mode,
		drawUnderlying,
		customClip,
	} = params;

	if (mode === "out") {
		drawUnderlying(time, 1);
	}

	const corner = transition.direction || "topRight";

	const baseColor = transition.color || "#D43F8C";

	const lightBaseColorRGBA = blendWithWhite(baseColor, 0.7);

	let centerX: number;
	let centerY: number;
	switch (corner) {
		case "topLeft":
			centerX = 0;
			centerY = 0;
			break;
		case "topRight":
			centerX = canvasWidth;
			centerY = 0;
			break;
		case "bottomRight":
			centerX = canvasWidth;
			centerY = canvasHeight;
			break;
		default:
			centerX = 0;
			centerY = canvasHeight;
			break;
	}

	const elapsed =
		mode === "out" ? time - keyTime : time - keyTime - transitionDuration;
	const progress = Math.min(elapsed / transitionDuration, 1);
	const easedProgress = interpolate(
		{
			x: 0,
			y: 0,
		},
		{
			x: 1,
			y: 1,
		},
		progress,
		"easeInOutSin",
	);

	// Calculate the maximum radius to ensure it covers the entire canvas
	const maxRadius = Math.sqrt(canvasWidth ** 2 + canvasHeight ** 2);
	const radius = easedProgress * 2 * maxRadius;

	const innerRadius =
		easedProgress < 0.5 ? 0 : radius * (easedProgress - 0.4999);
	const midRadius = radius * (easedProgress + 0.1);

	if (innerRadius > 0) {
		customClip?.();
	}

	// Draw the transition arcs
	ctx.beginPath();
	ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
	ctx.fillStyle = "transparent";
	ctx.fill();

	ctx.beginPath();
	ctx.arc(centerX, centerY, midRadius, 0, Math.PI * 2);
	ctx.arc(centerX, centerY, innerRadius, 0, Math.PI * 2, true);
	ctx.fillStyle = baseColor;
	ctx.fill();

	ctx.beginPath();
	ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
	ctx.arc(centerX, centerY, midRadius, 0, Math.PI * 2, true);
	ctx.fillStyle = lightBaseColorRGBA;
	ctx.fill();

	ctx.clip();

	ctx.restore();
}

export function applyCornerWipe(params: TransitionParams): any {
	const {
		ctx,
		time,
		transitionDuration,
		keyTime,
		canvasWidth,
		canvasHeight,
		transition,
		backgroundVisible,
		mode,
		drawUnderlying,
		customClip,
	} = params;

	let progress: number;

	// Calculate progress based on mode and elapsed time
	if (mode === "in") {
		// For "in" transition, progress goes from 0 to 1 as we approach keyTime
		const elapsed = time - (keyTime - transitionDuration);
		progress = Math.max(0, Math.min(elapsed / transitionDuration, 1));
	} else {
		// For "out" transition, progress goes from 0 to 1 as we move away from keyTime
		const elapsed = time - keyTime;
		progress = Math.min(elapsed / transitionDuration, 1);
	}

	const easedProgress = interpolate(
		{ x: 0, y: 0 },
		{ x: 1, y: 1 },
		progress,
		"easeInOutSin",
	);
	const maxRadius =
		Math.sqrt(canvasWidth * canvasWidth + canvasHeight * canvasHeight) +
		100;
	const radius = easedProgress * maxRadius;

	const corner = transition.direction || "topRight";
	let centerX: number;
	let centerY: number;

	switch (corner) {
		case "topLeft":
			centerX = 0;
			centerY = 0;
			break;
		case "topRight":
			centerX = canvasWidth;
			centerY = 0;
			break;
		case "bottomRight":
			centerX = canvasWidth;
			centerY = canvasHeight;
			break;
		default:
			centerX = 0;
			centerY = canvasHeight;
			break;
	}

	// Draw the underlying content first
	if (mode === "out") {
		drawUnderlying(time, 1);
	}

	ctx.save();

	// Create a clipping path
	ctx.beginPath();

	if (mode === "in") {
		// ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
		ctx.ellipse(centerX, centerY, radius, radius, 0, 0, Math.PI * 2);
	} else {
		// For "out" transition, we clip to the shrinking circle
		for (let angle = 0; angle <= Math.PI * 2; angle += Math.PI / 180) {
			const x = centerX + radius * Math.cos(angle);
			const y = centerY + radius * Math.sin(angle);
			ctx.lineTo(x, y);
		}
	}

	ctx.closePath();

	// Apply the clip
	ctx.clip();

	customClip?.();

	ctx.restore();

	return null;
}

export function applyStackTransition(params: TransitionParams): any {
	const {
		ctx,
		time,
		transitionDuration,
		keyTime,
		canvasWidth,
		canvasHeight,
		transition,
		mode,
		drawUnderlying,
		customClip,
	} = params;
	const edge = transition.direction || "right";

	let progress: number;
	// Calculate progress based on mode
	if (mode === "in") {
		const elapsed = time - (keyTime - transitionDuration);
		progress = Math.min(elapsed / transitionDuration, 1);
	} else {
		// For "out" mode
		const elapsed = time - keyTime;
		progress = Math.min(elapsed / transitionDuration, 1);
	}

	const easedProgress = interpolate(
		{ x: 0, y: 0 },
		{ x: 1, y: 1 },
		progress,
		"easeOutQuint",
	);

	let translateX = 0;
	let translateY = 0;

	drawUnderlying(time, 1);

	// Apply the same direction logic for both in and out transitions
	switch (edge) {
		case "top":
			translateY = -canvasHeight + easedProgress * canvasHeight;
			break;
		case "bottom":
			translateY = canvasHeight - easedProgress * canvasHeight;
			break;
		case "left":
			translateX = -canvasWidth + easedProgress * canvasWidth;
			break;
		case "right":
			translateX = canvasWidth - easedProgress * canvasWidth;
			break;
	}

	ctx.save();

	ctx.translate(translateX, translateY);

	customClip?.();

	ctx.restore();

	return null;
}

export function applyColorWipeTransition(params: TransitionParams): any {
	const {
		ctx,
		time,
		transitionDuration,
		keyTime,
		canvasWidth,
		canvasHeight,
		transition,
		mode,
		drawUnderlying,
		customClip,
	} = params;
	const isRightDirection = transition.direction === "right";
	const primaryColor = transition.color || "#073cad";
	const secondaryColor = blendWithWhite(primaryColor, 0.7);
	const tertiaryColor = blendWithBlack(primaryColor, 0.3);

	// Calculate elapsed time based on transition mode
	const elapsed =
		mode === "in" ? time - (keyTime - transitionDuration) : time - keyTime;
	const progress = Math.max(0, Math.min(elapsed / transitionDuration, 1));

	// For "out" mode, we want to keep the same direction as "in" mode
	const adjustedProgress = progress;

	// Use the x value from interpolate for the effective progress
	const easedProgress = interpolate(
		{ x: 0, y: 0 },
		{ x: 1, y: 1 },
		adjustedProgress,
		"easeInOutSin",
	);

	// Make sure we're using the x value from the interpolation
	const effectiveProgress = easedProgress;

	customClip?.();

	ctx.save();

	if (effectiveProgress <= 0.3) {
		const phaseProgress = effectiveProgress / 0.3;
		const smallBlock1Width = Math.min(
			canvasWidth * 0.13,
			canvasWidth * phaseProgress * 0.13,
		);
		const smallBlock1Offset = Math.min(
			canvasWidth * 0.3,
			canvasWidth * phaseProgress * 0.3,
		);

		if (isRightDirection) {
			const bigBlockX = canvasWidth * (1 - phaseProgress);
			ctx.fillStyle = primaryColor;
			ctx.fillRect(bigBlockX, 0, smallBlock1Offset, canvasHeight);
			ctx.fillStyle = secondaryColor;
			ctx.fillRect(
				bigBlockX + smallBlock1Offset,
				0,
				smallBlock1Width,
				canvasHeight,
			);
			ctx.fillStyle = tertiaryColor;
			ctx.fillRect(
				bigBlockX + smallBlock1Offset + smallBlock1Width,
				0,
				canvasWidth - (smallBlock1Offset + smallBlock1Width),
				canvasHeight,
			);
		} else {
			const bigBlockX = -canvasWidth * (1 - phaseProgress);
			ctx.fillStyle = primaryColor;
			ctx.fillRect(
				canvasWidth + bigBlockX - smallBlock1Offset,
				0,
				smallBlock1Offset,
				canvasHeight,
			);
			ctx.fillStyle = secondaryColor;
			ctx.fillRect(
				canvasWidth + bigBlockX - smallBlock1Offset - smallBlock1Width,
				0,
				smallBlock1Width,
				canvasHeight,
			);
			ctx.fillStyle = tertiaryColor;
			ctx.fillRect(
				0,
				0,
				canvasWidth + bigBlockX - smallBlock1Offset - smallBlock1Width,
				canvasHeight,
			);
		}
	} else if (effectiveProgress <= 0.5) {
		const phaseProgress = (effectiveProgress - 0.3) / 0.2;
		const smallBlock1Width = canvasWidth * 0.13;
		const smallBlock1InitialOffset = canvasWidth * 0.3;
		const firstBlockProgress = interpolate(
			{ x: 0, y: 0 },
			{ x: 1, y: 1 },
			phaseProgress,
			"linear",
		);
		const secondBlockStartPoint = 0.8;
		const secondBlockProgress = Math.max(
			0,
			(phaseProgress - secondBlockStartPoint) /
				(1 - secondBlockStartPoint),
		);
		const secondBlockEasedProgress = interpolate(
			{ x: 0, y: 0 },
			{ x: 1, y: 1 },
			secondBlockProgress,
			"linear",
		);

		if (isRightDirection) {
			const smallBlock1Offset =
				smallBlock1InitialOffset * (1 - firstBlockProgress) -
				smallBlock1Width * firstBlockProgress;
			const secondBlockWidth = canvasWidth * 0.13;
			const secondBlockInitialOffset = canvasWidth * 0.4;
			const secondBlockX =
				canvasWidth +
				secondBlockWidth -
				secondBlockInitialOffset * secondBlockEasedProgress;

			ctx.fillStyle = primaryColor;
			ctx.fillRect(
				0,
				0,
				Math.max(smallBlock1Offset + smallBlock1Width, 0),
				canvasHeight,
			);
			if (smallBlock1Offset + smallBlock1Width > 0) {
				ctx.fillStyle = secondaryColor;
				ctx.fillRect(
					smallBlock1Offset,
					0,
					smallBlock1Width,
					canvasHeight,
				);
			}
			if (phaseProgress >= secondBlockStartPoint) {
				ctx.fillStyle = tertiaryColor;
				ctx.fillRect(secondBlockX, 0, secondBlockWidth, canvasHeight);
			}
			ctx.fillStyle = tertiaryColor;
			ctx.fillRect(
				Math.max(smallBlock1Offset + smallBlock1Width, 0),
				0,
				canvasWidth - Math.max(smallBlock1Offset + smallBlock1Width, 0),
				canvasHeight,
			);
		} else {
			const smallBlock1Offset =
				smallBlock1InitialOffset * (1 - firstBlockProgress) -
				smallBlock1Width * firstBlockProgress;
			const secondBlockWidth = canvasWidth * 0.13;
			const secondBlockInitialOffset = canvasWidth * 0.4;
			const secondBlockX =
				-secondBlockWidth +
				secondBlockInitialOffset * secondBlockEasedProgress;

			ctx.fillStyle = primaryColor;
			ctx.fillRect(
				canvasWidth - Math.max(smallBlock1Offset + smallBlock1Width, 0),
				0,
				Math.max(smallBlock1Offset + smallBlock1Width, 0),
				canvasHeight,
			);
			if (smallBlock1Offset + smallBlock1Width > 0) {
				ctx.fillStyle = secondaryColor;
				ctx.fillRect(
					canvasWidth - smallBlock1Offset - smallBlock1Width,
					0,
					smallBlock1Width,
					canvasHeight,
				);
			}
			if (phaseProgress >= secondBlockStartPoint) {
				ctx.fillStyle = tertiaryColor;
				ctx.fillRect(secondBlockX, 0, secondBlockWidth, canvasHeight);
			}
			ctx.fillStyle = tertiaryColor;
			ctx.fillRect(
				0,
				0,
				canvasWidth - Math.max(smallBlock1Offset + smallBlock1Width, 0),
				canvasHeight,
			);
		}
	} else {
		drawUnderlying(time, 1);

		const phaseProgress = (effectiveProgress - 0.5) / 0.5;
		const bigBlockWidth = canvasWidth;
		const maxSmallBlockWidth = canvasWidth * 0.13;
		const smallBlockWidth = maxSmallBlockWidth * (1 - phaseProgress);
		const maxOffset = canvasWidth * 0.1;
		const offset = maxOffset * (1 - phaseProgress);

		if (isRightDirection) {
			const bigBlockX = -canvasWidth * phaseProgress;
			ctx.fillStyle = tertiaryColor;
			ctx.fillRect(bigBlockX, 0, bigBlockWidth, canvasHeight);
			if (smallBlockWidth > 0) {
				ctx.fillRect(
					bigBlockX + bigBlockWidth - smallBlockWidth - offset,
					0,
					smallBlockWidth,
					canvasHeight,
				);
			}
		} else {
			const bigBlockX = canvasWidth * phaseProgress;
			ctx.fillStyle = tertiaryColor;
			ctx.fillRect(bigBlockX, 0, bigBlockWidth, canvasHeight);
			if (smallBlockWidth > 0) {
				ctx.fillRect(
					bigBlockX + offset,
					0,
					smallBlockWidth,
					canvasHeight,
				);
			}
		}
	}

	ctx.restore();
	return null;
}
